import { useState } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { EmeraldAI } from '../components/AIConcierge';
import { TripPlannerQuiz } from '../components/TripPlannerQuiz';
import { SEOHead } from '../components/SEO';
import { Sparkles, MessageCircle, MapPin, Calendar, Utensils, Mountain, Route } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const features = [
  {
    icon: MessageCircle,
    title: 'Instant Answers',
    description: 'Get immediate responses about rooms, amenities, check-in times, and policies'
  },
  {
    icon: MapPin,
    title: 'Local Expert',
    description: 'Discover hidden gems, best viewpoints, and authentic experiences in Koraput'
  },
  {
    icon: Calendar,
    title: 'Trip Planning',
    description: 'Get personalized multi-day itineraries based on your interests and budget'
  },
  {
    icon: Utensils,
    title: 'Dining Guide',
    description: 'Restaurant recommendations, local cuisine tips, and dietary accommodations'
  },
  {
    icon: Mountain,
    title: 'Activity Suggestions',
    description: 'From trekking Deomali to visiting tribal markets - tailored to your pace'
  },
  {
    icon: Sparkles,
    title: 'Booking Assistance',
    description: 'Help finding the perfect room and exclusive direct booking benefits'
  }
];

export default function EmeraldAIPage() {
  const [activeTab, setActiveTab] = useState('chat');
  const [showTripPlanner, setShowTripPlanner] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="emerald-ai-page">
      <SEOHead 
        title="Emerald AI | Your Personal Koraput Guide | The Hotel Emerald"
        description="Meet Emerald AI - your intelligent travel companion for exploring Koraput. Get personalized trip plans, room recommendations, and local insights."
        keywords="AI concierge, hotel assistant, Koraput guide, trip planner, travel AI"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-32 pb-12 bg-gradient-to-b from-emerald-900 via-emerald-800 to-background overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700/50 rounded-full mb-6"
            >
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-emerald-100 text-sm">Powered by Advanced AI</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6"
            >
              Meet <span className="text-gold">Emerald AI</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg md:text-xl text-emerald-100 mb-6 sm:mb-8 px-2"
            >
              Your personal guide to The Hotel Emerald and the breathtaking Koraput region. 
              Plan your perfect trip, discover hidden gems, and get instant answers — all powered by AI.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap justify-center gap-3 sm:gap-4"
            >
              <button 
                onClick={() => {
                  setActiveTab('chat');
                  document.getElementById('ai-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-emerald text-sm sm:text-base"
                data-testid="start-chatting-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Start Chatting
              </button>
              <button 
                onClick={() => {
                  setActiveTab('planner');
                  document.getElementById('ai-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-gold text-sm sm:text-base"
                data-testid="plan-trip-btn"
              >
                <Route className="w-4 h-4 mr-2" />
                Plan My Trip
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Your AI-Powered Assistant</p>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-foreground">
              How Can I Help You?
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card border border-border rounded-xl p-4 sm:p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center mb-3 sm:mb-4">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Section with Tabs */}
      <section id="ai-section" className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-6 sm:mb-8"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">AI-Powered Tools</p>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-foreground">
              Choose Your Experience
            </h2>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-lg mx-auto grid-cols-2 mb-8 h-auto p-1">
              <TabsTrigger 
                value="chat" 
                className="flex items-center justify-center gap-2 py-3 text-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                data-testid="tab-chat"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Chat with AI</span>
              </TabsTrigger>
              <TabsTrigger 
                value="planner" 
                className="flex items-center justify-center gap-2 py-3 text-sm data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
                data-testid="tab-planner"
              >
                <Route className="w-4 h-4" />
                <span>Plan My Trip</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="chat">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto"
              >
                <p className="text-center text-muted-foreground mb-6">
                  Ask me anything about The Hotel Emerald, Koraput attractions, travel tips, or local recommendations.
                </p>
                <EmeraldAI fullPage={true} />
              </motion.div>
            </TabsContent>

            <TabsContent value="planner">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-2xl mx-auto"
              >
                <div className="text-center mb-8">
                  <p className="text-muted-foreground mb-6">
                    Answer a few quick questions and get a personalized day-by-day itinerary for your Koraput adventure.
                  </p>
                  <button
                    onClick={() => setShowTripPlanner(true)}
                    className="btn-emerald inline-flex items-center gap-2"
                    data-testid="start-planner-btn"
                  >
                    <Route className="w-4 h-4" />
                    Start Trip Planner
                  </button>
                </div>
                
                {/* Trip Planner Benefits */}
                <div className="grid sm:grid-cols-3 gap-4 mt-8">
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <Calendar className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <h4 className="font-medium text-foreground mb-1">Customized Itinerary</h4>
                    <p className="text-xs text-muted-foreground">Day-by-day plans based on your preferences</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <MapPin className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <h4 className="font-medium text-foreground mb-1">Local Attractions</h4>
                    <p className="text-xs text-muted-foreground">Hidden gems and must-visit spots</p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <Sparkles className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                    <h4 className="font-medium text-foreground mb-1">AI-Generated</h4>
                    <p className="text-xs text-muted-foreground">Smart recommendations for your style</p>
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-emerald-900">
        <div className="container-custom text-center text-white">
          <h2 className="font-serif text-2xl sm:text-3xl mb-4">Ready to Book Your Stay?</h2>
          <p className="text-emerald-200 mb-6 max-w-xl mx-auto">
            Experience the perfect blend of luxury and nature at The Hotel Emerald
          </p>
          <a href="/book" className="btn-gold inline-flex items-center gap-2">
            Book Now
          </a>
        </div>
      </section>

      <Footer />

      {/* Trip Planner Modal */}
      <TripPlannerQuiz open={showTripPlanner} onOpenChange={setShowTripPlanner} />
    </div>
  );
}
